import type { ReploElement } from "schemas/generated/element";

import * as React from "react";
import { useCallback, useState } from "react";

import * as RadioGroup from "@radix-ui/react-radio-group";
import DeprecatedModal from "@v1/components/common/designSystem/DeprecatedModal";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { withElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import { setSelectedArchivedElementId } from "@v1/reducers/core-reducer";
import { useEditorDispatch } from "@v1/store/store";
import { useModal } from "@v2/store/modalStore";
import { elementRouter } from "@v2/trpc/routes/element";
import { capitalizeFirstLetter } from "replo-utils/lib/string";

import { formatVersionHistoryDateAndTime } from "../version-history/VersionCard";

export type PermanentlyDeleteElementModalProps = {
  element: ReploElement;
};

export const PermanentlyDeleteElementModal: React.FC<PermanentlyDeleteElementModalProps> =
  withElementEditorErrorContext(({ element }) => {
    const modal = useModal();
    const [shouldUnpublish, setShouldUnpublish] = useState(false);
    const utils = elementRouter.useUtils();
    const { mutate: deleteMutation, isPending: isLoading } =
      elementRouter.delete.useMutation();
    const dispatch = useEditorDispatch();

    const handleDeleteForever = useCallback(
      (element: ReploElement) => {
        deleteMutation(element.id, {
          onSuccess: () => {
            successToast(
              `${capitalizeFirstLetter(
                elementTypeToEditorData[element.type].singularDisplayName,
              )} deleted successfully`,
              `Your ${capitalizeFirstLetter(
                elementTypeToEditorData[element.type].singularDisplayName,
              )} has been permanently deleted`,
            );

            // Invalidate and select first element
            void utils.listArchived
              .fetch({ projectId: element.projectId })
              .then((elements) => {
                const firstElement = elements?.[0];
                if (firstElement) {
                  dispatch(setSelectedArchivedElementId(firstElement.id));
                } else {
                  dispatch(setSelectedArchivedElementId(null));
                }
              });
            void utils.listArchived.invalidate();
            modal.closeModal({ type: "permanentlyDeleteElementModal" });
          },
        });
      },
      [deleteMutation, utils.listArchived, modal, dispatch],
    );

    return (
      <DeprecatedModal
        isOpen
        onRequestClose={() => {
          return modal.closeModal({ type: "permanentlyDeleteElementModal" });
        }}
        closesOnOverlayClick={true}
        className="w-[490px]"
        onEnterKeyPress={() => handleDeleteForever(element)}
      >
        <div className="no-scrollbar max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <p className="text-default text-lg font-medium">
              Permanently Delete this{" "}
              {elementTypeToEditorData[element.type].singularDisplayName}
            </p>
            <p className="text-muted">This action cannot be undone</p>
            <div>
              <div className="border-muted-foreground flex flex-col gap-2 rounded-md border px-4 py-3">
                <div className="flex items-start gap-2">
                  <div className="text-default flex flex-col gap-1 pl-2">
                    {element.name}
                    <p className="text-muted text-xs">
                      {element.archivedAt
                        ? `Archived ${formatVersionHistoryDateAndTime(element.archivedAt)}`
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <RadioGroup.Root
                value={shouldUnpublish ? "delete" : "keep"}
                onClick={() => setShouldUnpublish(!shouldUnpublish)}
                className="mt-4 flex flex-col gap-2"
              >
                <div className="flex items-start gap-2">
                  <RadioGroup.Item
                    value="delete"
                    id="delete"
                    className="border-0.5 border-muted data-[state=checked]:bg-primary data-[state=checked]:border-primary relative mt-1 h-4 w-4 rounded-full bg-white"
                  >
                    <RadioGroup.Indicator className="absolute inset-0 flex items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-white" />
                  </RadioGroup.Item>
                  <label htmlFor="delete">
                    <span className="text-default">Select to confirm </span>
                  </label>
                </div>
              </RadioGroup.Root>
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-row justify-end gap-x-2">
          <DeprecatedButton
            size="base"
            variant="secondary"
            type="button"
            layoutClassName="w-24"
            onClick={() => {
              return modal.closeModal({
                type: "permanentlyDeleteElementModal",
              });
            }}
          >
            Cancel
          </DeprecatedButton>
          <DeprecatedButton
            size="base"
            variant="danger"
            type="submit"
            isLoading={isLoading}
            data-testid="delete-submit-button"
            disabled={!shouldUnpublish}
            onClick={() => {
              handleDeleteForever(element);
            }}
          >
            Delete
          </DeprecatedButton>
        </div>
      </DeprecatedModal>
    );
  });
